<template>
	<div class="p-4 md:p-6 flex-1 overflow-y-auto font-sans">
		<!-- ── Section Header ── -->
		<div
			class="flex items-center justify-between mb-6 pb-5 border-b border-primary-100 dark:border-dark-600">
			<div class="flex items-center gap-3">
				<div
					class="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-400/15 flex items-center justify-center flex-shrink-0">
					<svg
						class="w-5 h-5 text-primary-400"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
					</svg>
				</div>
				<div>
					<h1
						class="font-display text-xl md:text-2xl font-extrabold tracking-tight text-dark-700 dark:text-white">
						Event Management
					</h1>
					<p class="text-[13px] text-muted dark:text-muted-light mt-0.5">
						Edit event details
					</p>
				</div>
			</div>
		</div>

		<Breadcrumbs />

		<!-- ── Loading ── -->
		<div
			v-if="isLoadingEvent"
			class="flex flex-col items-center justify-center h-64 gap-4">
			<div
				class="w-12 h-12 rounded-full border-[3px] border-primary-100 dark:border-dark-500 border-t-primary-400 animate-spin" />
			<p class="text-[13px] text-muted dark:text-muted-light">
				Loading event data...
			</p>
		</div>

		<!-- ── Form ── -->
		<div v-else>
			<form @submit.prevent="submitForm" class="space-y-4">
				<!-- ══ CARD 1 — Image + Title + Location ══ -->
				<div
					class="bg-surface dark:bg-dark-700 rounded-card shadow-card border border-primary-100 dark:border-dark-600 p-5 md:p-6">
					<p
						class="text-[11px] font-bold uppercase tracking-widest text-muted dark:text-muted-light mb-5">
						Basic Info
					</p>
					<div class="flex flex-col md:flex-row gap-6">
						<!-- Image uploader -->
						<div
							class="flex flex-col justify-center items-center rounded-xl w-full md:w-64 h-52 border-2 border-dashed cursor-pointer transition-all duration-200 border-primary-200 dark:border-dark-500 hover:border-primary-400 dark:hover:border-primary-400 bg-primary-50/50 dark:bg-dark-600/50"
							@dragover.prevent
							@drop.prevent="handleDrop">
							<template v-if="!event.imageURL">
								<div
									class="w-12 h-12 rounded-xl bg-primary-100 dark:bg-dark-500 flex items-center justify-center mb-3">
									<svg
										class="w-6 h-6 text-primary-400"
										fill="none"
										stroke="currentColor"
										stroke-width="2"
										viewBox="0 0 24 24">
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1M16 12l-4-4m0 0l-4 4m4-4v12" />
									</svg>
								</div>
								<p
									class="text-[13px] text-muted dark:text-muted-light text-center leading-relaxed">
									Drop image to upload<br />or
									<span
										class="text-primary-400 font-semibold cursor-pointer hover:text-primary-600"
										@click="browseFiles"
										>browse</span
									>
								</p>
							</template>
							<template v-else>
								<img
									:src="event.imageURL"
									alt="Preview"
									class="w-full h-40 object-cover rounded-lg" />
								<button
									@click="resetImage"
									class="mt-2 text-[12px] font-semibold text-red-400 hover:text-red-600 transition-colors">
									Remove
								</button>
							</template>
							<input
								type="file"
								ref="fileInput"
								class="hidden"
								@change="handleImageUpload" />
						</div>

						<!-- Title + Location -->
						<div class="flex-1 flex flex-col gap-4">
							<div>
								<label class="form-label">
									<svg
										class="w-4 h-4"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24">
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M7 7h10M7 11h7M7 15h4" />
									</svg>
									Event Title
								</label>
								<input
									v-model="event.title"
									type="text"
									class="form-input"
									:class="{
										'border-red-400': errors.title,
										'border-primary-400': !errors.title && event.title,
									}" />
								<ErrorMessage :message="errors.title" v-if="errors.title" />
							</div>

							<div>
								<label class="form-label">
									<svg
										class="w-4 h-4"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24">
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
									</svg>
									Event Location
								</label>
								<input
									v-model="locationQuery"
									@input="fetchLocationSuggestions"
									type="text"
									class="form-input" />
								<ul
									v-if="locationSuggestions.length"
									class="mt-1.5 rounded-xl border border-primary-100 dark:border-dark-500 bg-surface dark:bg-dark-700 shadow-dropdown overflow-hidden max-h-40 overflow-y-auto">
									<li
										v-for="(suggestion, index) in locationSuggestions"
										:key="index"
										@click="selectLocation(suggestion)"
										class="px-3.5 py-2.5 text-[13px] text-dark-700 dark:text-white/80 cursor-pointer hover:bg-primary-50 dark:hover:bg-dark-600 transition-colors border-b border-primary-50 dark:border-dark-600 last:border-0">
										{{ suggestion.display_name }}
									</li>
								</ul>
								<LMap
									v-if="event.lat && event.lng"
									ref="mapRef"
									:zoom="zoom"
									:center="[event.lat, event.lng]"
									class="h-52 w-full rounded-xl border border-primary-100 dark:border-dark-500 mt-3"
									:use-global-leaflet="false"
									@click="setLocation">
									<LTileLayer
										url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
									<LMarker
										v-if="event.lat && event.lng"
										:lat-lng="[event.lat, event.lng]" />
								</LMap>
								<ErrorMessage
									:message="errors.location"
									v-if="errors.location" />
							</div>
						</div>
						<ImageCropperModal
							:open="cropperOpen"
							:src="cropperSrc"
							:file-name="cropperFileName"
							:aspect="1"
							@cancel="closeCropper"
							@done="onCropDone" />
					</div>
				</div>

				<div class="form-divider" />

				<!-- ══ CARD 2 — Date & Time ══ -->
				<div
					class="bg-surface dark:bg-dark-700 rounded-card shadow-card border border-primary-100 dark:border-dark-600 p-5 md:p-6">
					<p
						class="text-[11px] font-bold uppercase tracking-widest text-muted dark:text-muted-light mb-5">
						Date & Time
					</p>
					<div class="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
						<div>
							<label class="form-label">
								<svg
									class="w-4 h-4"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
								</svg>
								Start Date
							</label>
							<input
								v-model="event.date"
								type="date"
								class="form-input"
								:class="{ 'border-red-400': errors.date }" />
							<ErrorMessage :message="errors.date" v-if="errors.date" />
						</div>
						<div>
							<label class="form-label">
								<svg
									class="w-4 h-4"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
								</svg>
								End Date
							</label>
							<input
								v-model="event.endDate"
								type="date"
								class="form-input"
								:class="{ 'border-red-400': errors.endDate }" />
							<ErrorMessage :message="errors.endDate" v-if="errors.endDate" />
						</div>
					</div>
					<div class="grid grid-cols-1 md:grid-cols-2 gap-5">
						<div>
							<label class="form-label">
								<svg
									class="w-4 h-4"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
								</svg>
								Start Time
							</label>
							<input
								v-model="event.startTime"
								type="time"
								class="form-input"
								:class="{ 'border-red-400': errors.startTime }" />
							<ErrorMessage
								:message="errors.startTime"
								v-if="errors.startTime" />
						</div>
						<div>
							<label class="form-label">
								<svg
									class="w-4 h-4"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
								</svg>
								End Time
							</label>
							<input
								v-model="event.endTime"
								type="time"
								@change="validateEventDateTime"
								class="form-input"
								:class="{ 'border-red-400': errors.endTime }" />
							<ErrorMessage :message="errors.endTime" v-if="errors.endTime" />
						</div>
					</div>
				</div>

				<div class="form-divider" />

				<!-- ══ CARD 3 — Pricing + Capacity + Organizer ══ -->
				<div
					class="bg-surface dark:bg-dark-700 rounded-card shadow-card border border-primary-100 dark:border-dark-600 p-5 md:p-6">
					<p
						class="text-[11px] font-bold uppercase tracking-widest text-muted dark:text-muted-light mb-5">
						Pricing & Capacity
					</p>

					<div class="flex flex-col gap-3 mb-4">
						<div
							v-for="(price, index) in event.prices"
							:key="index"
							class="flex flex-col md:flex-row items-stretch md:items-center gap-3 p-3.5 rounded-xl bg-primary-50/60 dark:bg-dark-600/60 border border-primary-100 dark:border-dark-500">
							<div class="flex-1">
								<input
									v-model="price.type"
									type="text"
									placeholder="Price type (e.g. VIP)"
									class="form-input !py-2"
									:class="{
										'border-red-400':
											errors.price && (!price.type || !isAmountFilled(price.amount)),
									}" />
							</div>
							<div class="flex-1">
								<input
									v-model="price.amount"
									type="number"
									step="0.01"
									placeholder="Amount (€)"
									class="form-input !py-2"
									:class="{
										'border-red-400':
											errors.price && (!price.type || !isAmountFilled(price.amount)),
									}" />
							</div>
							<button
								type="button"
								@click="removePrice(index)"
								class="flex items-center justify-center w-9 h-9 rounded-xl flex-shrink-0 bg-red-50 dark:bg-red-950/30 text-red-400 hover:bg-red-100 dark:hover:bg-red-950/50 transition-colors">
								<i class="pi pi-trash text-[14px]" />
							</button>
						</div>
					</div>

					<ErrorMessage :message="errors.price" v-if="errors.price" />

					<button
						type="button"
						@click="addPrice"
						class="flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-semibold border-2 border-dashed border-primary-300 dark:border-primary-400/40 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-400/10 hover:border-primary-400 transition-all duration-200 mb-1">
						<svg
							class="w-4 h-4"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2.5"
								d="M12 4v16m8-8H4" />
						</svg>
						Add New Price
					</button>
					<p class="text-[11px] text-muted dark:text-muted-light mb-6">
						You can add multiple ticket types (e.g. Regular, VIP, Early Bird).
					</p>

					<div class="grid grid-cols-1 md:grid-cols-2 gap-5">
						<div>
							<label class="form-label">
								<svg
									class="w-4 h-4"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
								</svg>
								Expected Attendees
							</label>
							<input
								v-model="event.capacity"
								type="number"
								class="form-input"
								:class="{ 'border-red-400': errors.capacity }" />
							<ErrorMessage :message="errors.capacity" v-if="errors.capacity" />
						</div>
						<div>
							<label class="form-label">
								<svg
									class="w-4 h-4"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5" />
								</svg>
								Event Organizer
							</label>
							<select
								v-model.number="event.organizer"
								v-if="forAdmin()"
								class="form-input"
								:class="{
									'border-red-400': errors.organizer,
									'border-primary-400': !errors.organizer && organizations,
								}">
								<option value="" disabled>Select an Organizer</option>
								<option
									v-if="
										event.organizer &&
										!organizations.some((org) => org.ID === event.organizer)
									"
									:value="event.organizer"
									disabled>
									Selected organizer (unavailable)
								</option>
								<option
									v-for="org in organizations"
									:key="org.ID"
									:value="org.ID">
									{{ org.Name }}
								</option>
							</select>
							<input
								v-if="forClient()"
								type="text"
								:value="userInfo.name"
								disabled
								class="form-input opacity-60 cursor-not-allowed bg-primary-50 dark:bg-dark-600" />
							<input
								v-if="forClient()"
								type="hidden"
								v-model.number="event.organizer" />
							<ErrorMessage
								:message="errors.organizer"
								v-if="errors.organizer" />
						</div>
					</div>
				</div>

				<div class="form-divider" />

				<!-- ══ CARD 4 — Categories + Description ══ -->
				<div
					class="bg-surface dark:bg-dark-700 rounded-card shadow-card border border-primary-100 dark:border-dark-600 p-5 md:p-6">
					<p
						class="text-[11px] font-bold uppercase tracking-widest text-muted dark:text-muted-light mb-5">
						Categories & Description
					</p>

					<div class="mb-6">
						<label class="form-label">
							<svg
								class="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
							</svg>
							Choose Category
							<span
								class="ml-auto text-[10px] font-bold text-muted dark:text-muted-light"
								>max 3</span
							>
						</label>
						<select
							v-model="selectedCategory"
							@change="addCategory"
							:disabled="event.selectedCategories.length >= 3"
							class="form-input"
							:class="{
								'border-red-400': errors.categories,
								'border-primary-400': !errors.categories && categories,
								'opacity-50 cursor-not-allowed':
									event.selectedCategories.length >= 3,
							}">
							<option value="" disabled>Select a category</option>
							<option
								v-for="category in availableCategories"
								:key="String(category?.ID ?? category?.id ?? '')"
								:value="String(category?.ID ?? category?.id ?? '')"
							>
								{{ category.name }}
							</option>
						</select>
						<div class="flex flex-wrap gap-2 mt-3">
							<span
								v-for="categoryId in event.selectedCategories"
								:key="categoryId"
								class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold bg-primary-400 text-white">
								{{ getCategoryName(categoryId) }}
								<button
									@click="removeCategory(categoryId)"
									class="w-4 h-4 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/40 transition-colors text-white">
									&times;
								</button>
							</span>
						</div>
						<p
							v-if="event.selectedCategories.length >= 3"
							class="mt-2 text-[12px] text-muted dark:text-muted-light bg-primary-50 dark:bg-dark-600 rounded-lg px-3 py-2 text-center">
							Maximum 3 categories selected
						</p>
						<ErrorMessage
							:message="errors.categories"
							v-if="errors.categories" />
					</div>

					<div>
						<label class="form-label">
							<svg
								class="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M4 6h16M4 10h16M4 14h10" />
							</svg>
							Reach out to your Audience
						</label>
						<textarea
							v-model="event.description"
							rows="1"
							style="
								overflow-y: auto;
								resize: none;
								max-height: 400px;
								min-height: 100px;
							"
							@input="handleTextarea"
							class="form-input"
							:class="
								isValid
									? 'border-primary-400 focus:ring-primary-400/20'
									: 'border-red-400 focus:ring-red-400/20'
							" />
						<ErrorMessage
							:message="descriptionError.description"
							v-if="descriptionError.description" />

						<div class="mt-2 flex items-center justify-between">
							<div class="text-[12px]">
								<span
									class="font-semibold"
									:class="
										charCount > maxChars
											? 'text-red-500'
											: charCount < minChars
											? 'text-accent-600 dark:text-accent-400'
											: 'text-primary-600 dark:text-primary-400'
									">
									{{ charCount }}
								</span>
								<span class="text-muted dark:text-muted-light">
									/ {{ maxChars }} characters</span
								>
							</div>
							<div class="text-[11px]">
								<span
									v-if="charCount < minChars"
									class="text-accent-600 dark:text-accent-400"
									>{{ minimumChar }}</span
								>
								<span v-if="charCount > maxChars" class="text-red-500">{{
									exceedMaxChar
								}}</span>
							</div>
						</div>
						<div
							class="mt-1.5 h-1 rounded-full bg-primary-100 dark:bg-dark-500 overflow-hidden">
							<div
								class="h-full rounded-full transition-all duration-300"
								:class="
									charCount > maxChars
										? 'bg-red-400'
										: charCount < minChars
										? 'bg-accent-400'
										: 'bg-primary-400'
								"
								:style="{
									width: Math.min((charCount / maxChars) * 100, 100) + '%',
								}" />
						</div>
					</div>
				</div>

				<div class="form-divider" />

				<!-- ══ CARD 5 — Organizer Logos ══ -->
				<div
					class="bg-surface dark:bg-dark-700 rounded-card shadow-card border border-primary-100 dark:border-dark-600 p-5 md:p-6">
					<div class="flex items-center gap-3 mb-5">
						<input
							type="checkbox"
							v-model="event.addOtherOrganizers"
							class="w-4 h-4 rounded accent-primary-400 cursor-pointer" />
						<label
							class="text-[13px] font-semibold text-dark-700 dark:text-white cursor-pointer">
							Add Other Organizers' Logos
						</label>
					</div>

					<div v-if="event.addOtherOrganizers" class="flex flex-wrap gap-4">
						<div
							v-for="(logo, index) in event.organizerLogos"
							:key="index"
							class="relative w-28 h-28 rounded-xl border-2 border-dashed border-primary-200 dark:border-dark-500 flex items-center justify-center cursor-pointer hover:border-primary-400 dark:hover:border-primary-400 bg-primary-50/50 dark:bg-dark-600/50 transition-all"
							:class="{
								'opacity-60 pointer-events-none': uploadingIndex === index,
							}"
							@dragover.prevent
							@drop.prevent="handleDrop(index, $event)">
							<input
								type="file"
								accept="image/png,image/jpeg,image/svg+xml"
								class="absolute inset-0 opacity-0 cursor-pointer"
								@change="handleLogoUpload(index, $event)" />
							<span
								v-if="!logo"
								class="font-display text-2xl font-light text-muted dark:text-muted-light"
								>+</span
							>
							<img
								v-if="event.organizerLogoURLs[index]"
								:src="event.organizerLogoURLs[index].LogoURL"
								alt="Organizer Logo"
								class="absolute inset-0 w-full h-full object-cover rounded-xl" />
							<button
								v-if="logo || event.organizerLogoURLs[index]"
								type="button"
								@click="removeOrganizerLogo(index)"
								class="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-white dark:bg-dark-700 shadow-sm flex items-center justify-center hover:bg-red-50 transition-colors">
								<svg
									class="w-3 h-3 text-muted dark:text-muted-light"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
							<div
								v-if="uploadingIndex === index"
								class="absolute inset-0 rounded-xl skeleton-shimmer z-20" />
						</div>
					</div>

					<p class="text-[11px] text-muted dark:text-muted-light mt-3">
						Supported formats: JPG, PNG, SVG
					</p>
					<div
						v-if="event.organizerLogos.length < 8 && event.addOtherOrganizers"
						class="mt-3">
						<button
							type="button"
							@click="addOrganizerLogo"
							class="text-[13px] font-semibold text-primary-400 hover:text-primary-600 dark:hover:text-primary-300 transition-colors">
							+ Add Another Logo
						</button>
					</div>
				</div>

				<div class="form-divider" />

				<!-- ══ Action Buttons ══ -->
				<div class="flex flex-col md:flex-row gap-3">
					<button
						type="submit"
						:disabled="
							isSubmitting ||
							!isValidForm ||
							charCount < minChars ||
							charCount > maxChars
						"
						class="flex-1 font-display font-bold text-[14px] py-3.5 rounded-xl bg-dark-700 dark:bg-primary-400 text-white hover:bg-primary-600 dark:hover:bg-primary-600 hover:-translate-y-0.5 shadow-card hover:shadow-teal disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none transition-all duration-200">
						<span
							v-if="!isLoading"
							class="flex items-center justify-center gap-2">
							<svg
								class="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2.5"
									d="M5 13l4 4L19 7" />
							</svg>
							Update Event
						</span>
						<span v-else class="flex items-center justify-center gap-2">
							<svg class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
								<circle
									class="opacity-25"
									cx="12"
									cy="12"
									r="10"
									stroke="currentColor"
									stroke-width="4" />
								<path
									class="opacity-75"
									fill="currentColor"
									d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
							</svg>
							Processing...
						</span>
					</button>

					<button
						type="button"
						@click="cancelEdit"
						class="px-8 font-display font-bold text-[14px] py-3.5 rounded-xl bg-primary-50 dark:bg-dark-600 border border-primary-100 dark:border-dark-500 text-dark-400 dark:text-white/70 hover:bg-primary-100 dark:hover:bg-dark-500 hover:-translate-y-0.5 transition-all duration-200">
						Cancel
					</button>
				</div>
			</form>
		</div>
	</div>
</template>
<style scoped>
@keyframes shimmer {
    0% {
        background-position: -400px 0;
    }

    100% {
        background-position: 400px 0;
    }
}

.skeleton-shimmer {
    background: linear-gradient(90deg,
            #f0f0f0 25%,
            #e6e6e6 37%,
            #f0f0f0 63%);
    background-size: 400px 100%;
    animation: shimmer 1.4s ease infinite;
}
</style>
<script>
import { reactive, ref, onMounted, computed, watch } from "vue";
import { LMap, LTileLayer, LMarker } from "@vue-leaflet/vue-leaflet";
import "leaflet/dist/leaflet.css";
import { showToast } from "@/constants/toast";
import { useToast } from "vue-toast-notification";
import "vue-toast-notification/dist/theme-sugar.css";
import axios from "axios";
import ErrorMessage from "@/components/messages/ErrorMessage.vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { apiDataService } from "@/services/apiDataService";
import Breadcrumbs from "@/components/Breadcrumbs.vue";
import ImageCropperModal from "@/components/ImageCropperModal.vue";

export default {
	components: { LMap, LTileLayer, LMarker, ErrorMessage, Breadcrumbs, ImageCropperModal },
	setup() {
		const store = useStore();
		const route = useRoute();
		const router = useRouter();
		const toast = useToast();
		const eventId = route.params.id;
		const isSubmitting = ref(false);
		const isLoadingEvent = ref(true);
		const organizations = computed(() => store.state.organizer.organizers);
		const getOrganizers = () => store.dispatch("organizer/fetchAllOrganizers");
		const userInfo = computed(() => store.state.auth.user || {});
		const getInfo = () => store.dispatch("auth/fetchCurrentUser");
		const forAdmin = () => {
			if (!userInfo.value || !userInfo.value.user_type) return false;
			const type = Number(userInfo.value.user_type);
			return type !== 1 && type !== 2;
		};
		const forClient = () => {
			const type = Number(userInfo.value.user_type);
			return type !== 1 && type === 2;
		};

		const event = reactive({
			id: "",
			title: "",
			lat: 0,
			lng: 0,
			date: "",
			endDate: "",
			startTime: "",
			endTime: "",
			image: null,
			imageURL: "",
			organizer: 0,
			capacity: 0,
			prices: [{ type: "", amount: "" }],
			selectedCategories: [],
			addOtherOrganizers: false,
			organizerLogos: [],
			organizerLogoURLs: [],
			description: "",
			locationName: "",
		});
		const errors = reactive({
			title: "",
			location: "",
			date: "Start date cannot be in the past.",
			endDate: "",
			startTime: "",
			endTime: "",
			price: "",
			capacity: "",
			organizer: "",
			image: "",
			categories: "",
			description: "",
			organizerLogos: [],
		});
		const uploadingIndex = ref(null);
		const fileInput = ref(null);
		const selectedFiles = ref([]);
		const locationQuery = ref("");
		const locationSuggestions = ref([]);
		const zoom = ref(12);
		let timeout;
		const descriptionError = reactive({ description: "" });
		const eventWords = ref({ description: "" });
		const charCount = ref(0);
		const minChars = 100;
		const maxChars = 500;
		const isValid = ref(true);
		const exceedMaxChar = ref("");
		const minimumChar = ref("");

		const fetchEvent = async () => {
			try {
				isLoadingEvent.value = true;
				const cachedEvent = store.getters["event/getEventById"](eventId);
				if (cachedEvent) {
					loadEventData(cachedEvent);
				} else {
					await store.dispatch("event/fetchAllEvents");
					const freshEvent = store.getters["event/getEventById"](eventId);
					if (freshEvent) loadEventData(freshEvent);
					else throw new Error("Event not found");
				}
			} catch (error) {
				console.error("Error fetching event:", error);
				toast.error("Failed to load event data", { position: "top-right" });
				router.push("/admin/event/all");
			} finally {
				isLoadingEvent.value = false;
			}
		};

		const loadEventData = (eventData) => {
			const prices = eventData.tickets
				? eventData.tickets.map((ticket) => ({id: ticket.ID || "",type: ticket.Type || "",amount: ticket.Amount ?? "0",})): [{ type: "", amount: "" }];
			let organizerId = 0;
			if (
				typeof eventData.organizer === "object" &&
				eventData.organizer !== null
			)
				organizerId = parseInt(eventData.organizer.ID) || 0;
			else if (eventData.organizer)
				organizerId = parseInt(eventData.organizer) || 0;
			else organizerId = forClient() ? parseInt(userInfo.value?.id) || 0 : 0;
			Object.assign(event, {
				id: eventData.event_id || "",
				title: eventData.title || "",
				lat: parseFloat(eventData.latitude) || 0,
				lng: parseFloat(eventData.longitude) || 0,
				date: eventData.date ? eventData.date.split("T")[0] : "",
				endDate: eventData.end_date ? eventData.end_date.split("T")[0] : "",
				startTime: eventData.start_time || "",
				endTime: eventData.end_time || "",
				imageURL: eventData.image_url || "",
				organizer: organizerId,
				capacity: eventData.capacity || 0,
				description: eventData.description || "",
				locationName: eventData.location_name || "",
				selectedCategories: eventData.categories
					? eventData.categories.map((c) => String(c?.ID ?? c?.id ?? ""))
					: [],
				prices,
				addOtherOrganizers: eventData.organizer_logos
					? eventData.organizer_logos.length > 0
					: false,
				organizerLogoURLs: eventData.organizer_logos || [],
			});
			locationQuery.value = eventData.location_name || "";
			event.organizerLogos =
				eventData.organizer_logos?.length > 0
					? Array(eventData.organizer_logos.length).fill(null)
					: [];
		};

		watch(
			() => userInfo.value,
			(newUserInfo) => {
				if (
					newUserInfo &&
					forClient() &&
					(!event.organizer || event.organizer === 0)
				)
					event.organizer = parseInt(newUserInfo.organization_id) || 0;
			},
			{ immediate: true, deep: true },
		);
		const fetchLocationSuggestions = () => {
			if (locationQuery.value.length < 3) return;
			clearTimeout(timeout);
			timeout = setTimeout(async () => {
				const res = await axios.get(
					"https://nominatim.openstreetmap.org/search",
					{ params: { q: locationQuery.value, format: "json", limit: 5 } },
				);
				locationSuggestions.value = res.data;
			}, 300);
		};
		const selectLocation = async (suggestion) => {
			event.lat = parseFloat(suggestion.lat);
			event.lng = parseFloat(suggestion.lon);
			locationQuery.value = suggestion.display_name;
			locationSuggestions.value = [];
			await getLocationNameFromCoordinates(event.lat, event.lng);
		};
		const setLocation = async (e) => {
			event.lat = e.latlng.lat;
			event.lng = e.latlng.lng;
			await getLocationNameFromCoordinates(event.lat, event.lng);
		};
		const getLocationNameFromCoordinates = async (lat, lng) => {
			try {
				const res = await axios.get(
					"https://nominatim.openstreetmap.org/reverse",
					{ params: { lat, lon: lng, format: "json", addressdetails: 1 } },
				);
				event.locationName = res.data.display_name;
				locationQuery.value = res.data.display_name;
			} catch (err) {
				console.error("Error getting location name:", err);
				event.locationName = "";
			}
		};
		const validateTitle = () => {
			errors.title = event.title.trim() ? "" : "Title is required";
		};
		const validateLocation = () => {
			errors.location =
				event.lat && event.lng ? "" : "Please select a location";
		};
		const validateDate = () => {
			const now = new Date();
			const selectedDateTime = new Date(
				`${event.date}T${event.startTime || "00:00"}`,
			);
			errors.date =
				selectedDateTime < now ? "Start date/time cannot be in the past." : "";
		};
		const validateEndDate = () => {
			errors.endDate =
				new Date(event.endDate) < new Date(event.date)
					? "End date must be after start date."
					: "";
		};
		watch(
			[
				() => event.date,
				() => event.startTime,
				() => event.endTime,
				() => event.endDate,
			],
			() => {
				validateEventDateTime();
			},
		);
		const validateEventDateTime = () => {
			const startDateTime = new Date(`${event.date}T${event.startTime}`);
			const endDateTime = new Date(
				`${event.endDate || event.date}T${event.endTime}`,
			);
			const diffInHours = (endDateTime - startDateTime) / (1000 * 60 * 60);
			errors.endTime =
				diffInHours <= 2
					? "⚠️ End time must be at least 2 hours after start time."
					: "";
		};
		const setMessage = (type, text) => {
			exceedMaxChar.value = "";
			minimumChar.value = "";
			if (type === "ExceedMaxChar") exceedMaxChar.value = text;
			if (type === "MinimumChar") minimumChar.value = text;
		};
		watch(
			() => event.description,
			(newVal) => {
				charCount.value = newVal?.length || 0;
				eventWords.value.description = newVal || "";
				if (charCount.value < minChars) {
					setMessage(
						"MinimumChar",
						`⚠️ Description must be at least 100 - 500 characters.`,
					);
					isValid.value = false;
				} else if (charCount.value > maxChars) {
					setMessage(
						"ExceedMaxChar",
						`❌ Description cannot exceed 500 characters.`,
					);
					isValid.value = false;
				} else {
					exceedMaxChar.value = "";
					minimumChar.value = "";
					isValid.value = true;
				}
			},
			{ immediate: true },
		);
		const handleTextarea = (e) => {
			e.target.style.height = "auto";
			e.target.style.height = e.target.scrollHeight + "px";
			if (e.target.value.length > maxChars)
				setMessage(
					"ExceedMaxChar",
					`❌ Description cannot exceed 500 characters.`,
				);
			eventWords.value.description = e.target.value;
			charCount.value = e.target.value.length;
			if (charCount.value < minChars && charCount.value < maxChars) {
				isValid.value = false;
				setMessage(
					"MinimumChar",
					`⚠️ Description must be at least 100 - 500 characters.`,
				);
			} else {
				isValid.value = true;
			}
		};
		// Explicit "is this amount actually filled in" check.
		// Using a plain truthy check on p.amount (e.g. `!p.amount`) breaks for
		// a free ticket priced at 0, because a numeric 0 is falsy even though
		// it's a perfectly valid, intentional price. This checks for presence
		// of a real numeric value instead, so 0 is always accepted and stays editable.
		const isAmountFilled = (amount) => {
			if (amount === null || amount === undefined) return false;
			const str = String(amount).trim();
			if (str === "") return false;
			return !isNaN(parseFloat(str));
		};
		const validatePrice = () => {
			let hasEmpty = false;
			let hasComplete = false;
			event.prices.forEach((p) => {
				const amountFilled = isAmountFilled(p.amount);
				if (p.type.trim() && amountFilled) hasComplete = true;
				else if (p.type.trim() || amountFilled) hasEmpty = true;
			});
			if (hasEmpty) {
				errors.price =
					"All price entries must have both type and amount or be empty";
				return false;
			}
			errors.price = hasComplete
				? ""
				: "At least one price type and amount must be filled";
			return hasComplete;
		};
		const validateCapacity = () => {
			errors.capacity =
				event.capacity > 0 ? "" : "Capacity must be a positive number";
		};
		const validateOrganizer = () => {
			if (
				(!event.organizer || event.organizer === 0) &&
				userInfo.value?.organization_id
			)
				event.organizer = parseInt(userInfo.value.organization_id);
			errors.organizer = event.organizer ? "" : "Organizer is required";
		};
		const validateCategories = () => {
			errors.categories = event.selectedCategories.length
				? ""
				: "Please select at least one category";
		};
		const validateDescription = () => {
			errors.description = event.description.length
				? ""
				: "You must write something that describe your event";
		};
		const validateForm = () => {
			validateTitle();
			validateLocation();
			validateDate();
			validateEndDate();
			validatePrice();
			validateCapacity();
			validateOrganizer();
			validateCategories();
			validateDescription();
			const mainErrors = {
				title: errors.title,
				location: errors.location,
				date: errors.date,
				endDate: errors.endDate,
				startTime: errors.startTime,
				endTime: errors.endTime,
				price: errors.price,
				capacity: errors.capacity,
				organizer: errors.organizer,
				categories: errors.categories,
				description: errors.description,
			};
			return Object.values(mainErrors).every((e) => !e);
		};
		const isValidForm = computed(() => validateForm());
		const isLoading = computed(() => store.state.event.loading);
		const categories = computed(() => store.state.category.categories);
		const selectedCategory = ref("");
		const availableCategories = computed(() =>
			categories.value.filter(
				(category) =>
				!event.selectedCategories.includes(
					String(category?.ID ?? category?.id ?? ""),
				),
			),
		);
		const getCategories = () => store.dispatch("category/fetchAllCategories");
		const addCategory = () => {
			if (
				selectedCategory.value &&
				!event.selectedCategories.includes(selectedCategory.value) &&
				event.selectedCategories.length < 3
			) {
				event.selectedCategories.push(selectedCategory.value);
				selectedCategory.value = "";
			}
		};
		const removeCategory = (id) => {
			event.selectedCategories = event.selectedCategories.filter(
				(cat) => cat !== id,
			);
		};
		const getCategoryName = (id) => {
			const cat = categories.value.find(
				(c) => String(c?.ID ?? c?.id ?? "") === id
			);
			return cat ? cat.Name || cat.name : "Unknown Category";
		};
		const addPrice = () => {
			if (event.prices.length > 0) {
				const lastPrice = event.prices[event.prices.length - 1];
				if (!lastPrice.type.trim() || !isAmountFilled(lastPrice.amount)) {
					errors.price =
						"Please complete the current price before adding a new one";
					return;
				}
			}
			event.prices.push({ type: "", amount: "" });
			errors.price = "";
		};
		const removePrice = (index) => {
			if (event.prices.length <= 1) {
				toast.warning("You need at least one price entry", {
					position: "top-right",
				});
				return;
			}
			event.prices.splice(index, 1);
			validatePrice();
		};
		const browseFiles = () => {
			fileInput.value?.click();
		};
		// ── Image cropper ──
		const cropperOpen = ref(false);
		const cropperSrc = ref("");
		const cropperFileName = ref("");

		const closeCropper = () => {
			if (cropperSrc.value) URL.revokeObjectURL(cropperSrc.value);
			cropperSrc.value = "";
			cropperOpen.value = false;
			if (fileInput.value) fileInput.value.value = null;
		};
		const handleDrop = (event) => {
			const file = event.dataTransfer.files[0];
			if (!file) return;
			handleImageUpload({ target: { files: [file] } });
		};

		const handleImageUpload = (e) => {
			const file = e.target.files?.[0];
			if (!file) return;
			if (!file.type.startsWith("image/")) {
				toast.warning("Please upload an image file.", { position: "top-right" });
				return;
			}
			if (file.size > 5 * 1024 * 1024) {
				toast.warning("File size exceeds 5MB.", { position: "top-right" });
				return;
			}
			cropperFileName.value = file.name;
			cropperSrc.value = URL.createObjectURL(file);
			cropperOpen.value = true;
		};

		// Cropper hands back the final File
		const onCropDone = (file) => {
			if (event.imageURL) URL.revokeObjectURL(event.imageURL);
			event.image = file;
			event.imageURL = URL.createObjectURL(file);
			closeCropper();
		};
		const resetImage = () => {
			if (event.imageURL) URL.revokeObjectURL(event.imageURL);
			event.image = null;
			event.imageURL = "";
		};
		const handleLogoUpload = async (index, e) => {
			const file = e.target.files[0];
			if (!file.type.startsWith("image/")) {
				errors.organizerLogos[index] = "Invalid image file.";
				return;
			}
			if (file.size > 5 * 1024 * 1024) {
				errors.organizerLogos[index] = "File too large.";
				return;
			}
			uploadingIndex.value = index;
			const localPreview = {
				ID: null,
				LogoURL: URL.createObjectURL(file),
				isLocal: true,
			};
			event.organizerLogoURLs = event.organizerLogoURLs.map((item, i) =>
				i === index ? localPreview : item,
			);
			if (!event.organizerLogoURLs[index])
				event.organizerLogoURLs = [...event.organizerLogoURLs, localPreview];
			try {
				const formData = new FormData();
				formData.append("organizer_logo", file);
				const response = await apiDataService.uploadEventImage(
					eventId,
					formData,
				);
				showToast("success", response.data.message);
			} catch (error) {
				console.error("Error uploading image:", error);
				showToast("error", "Failed to upload logo");
			} finally {
				uploadingIndex.value = null;
				e.target.value = "";
			}
		};
		const addOrganizerLogo = () => {
			if (event.organizerLogos.filter(Boolean).length < 5) {
				event.organizerLogos.push(null);
				event.organizerLogoURLs.push("");
			}
		};
		const removeOrganizerLogo = async (index) => {
			const logo = event.organizerLogoURLs[index];
			if (!logo?.ID) return;
			try {
				const response = await apiDataService.deleteEventImage(
					eventId,
					logo.ID,
				);
				showToast("success", response.data.message);
				event.organizerLogoURLs.splice(index, 1);
				event.organizerLogos.splice(index, 1);
			} catch (error) {
				console.error("Error deleting image:", error);
				showToast("error", "Failed to delete logo");
			}
		};
		const submitForm = async () => {
			if (!validateForm()) {
				toast.warning("Please fix all errors before submitting.", {
					position: "top-right",
				});
				return;
			}
			isSubmitting.value = true;
			const formData = new FormData();
			formData.append("id", event.id);
			formData.append("title", event.title);
			formData.append("latitude", event.lat.toString());
			formData.append("longitude", event.lng.toString());
			formData.append("location_name", event.locationName);
			formData.append("date", event.date);
			formData.append("end_date", event.endDate);
			formData.append("start_time", event.startTime);
			formData.append("end_time", event.endTime);
			formData.append("organizer", event.organizer);
			formData.append("capacity", event.capacity.toString());
			formData.append("description", event.description);
			event.prices.forEach((p, i) => {
				if (p.id) formData.append(`price_type_${i}_id`, p.id);
				formData.append(`price_type_${i}_type`, p.type);
				formData.append(`price_type_${i}_amount`, p.amount);
			});
			const uniqueCategories = [...new Set(event.selectedCategories)];
			uniqueCategories.forEach((categoryId, i) => {
				formData.append(`category_${i}`, categoryId);
			});
			if (event.image) formData.append("image", event.image);
			if (event.addOtherOrganizers)
				event.organizerLogos.forEach((logo, i) => {
					if (logo) formData.append(`organizer_logo_${i}`, logo);
				});
			try {
				store.commit("event/SET_LOADING", true);
				const response = await apiDataService.updateEvent(formData);
				if (response.code === 200) {
					await store.dispatch("event/fetchAllEvents");
					showToast("success", `Event updated successfully!`);
					router.push({ name: "eventList" });
				}
			} catch (error) {
				console.error("Error updating event:", error);
				store.commit(
					"event/SET_ERROR",
					error.message || "Failed to update event",
				);
				toast.error("Failed to update event. Please try again.", {
					position: "top-right",
				});
			} finally {
				isSubmitting.value = false;
				store.commit("event/SET_LOADING", false);
			}
		};
		const confirmDelete = () => {
			if (
				confirm(
					"Are you sure you want to delete this event? This action cannot be undone.",
				)
			)
				deleteEvent();
		};
		const deleteEvent = async () => {
			try {
				store.commit("event/SET_LOADING", true);
				const response = await apiDataService.deleteEvent(eventId);
				if (response.status === 200) {
					await store.dispatch("event/fetchAllEvents");
					toast.success("Event deleted successfully!", {
						position: "top-right",
					});
					router.push("/admin/event/all");
				}
			} catch (error) {
				console.error("Error deleting event:", error);
				store.commit(
					"event/SET_ERROR",
					error.message || "Failed to delete event",
				);
				toast.error("Failed to delete event. Please try again.", {
					position: "top-right",
				});
			} finally {
				store.commit("event/SET_LOADING", false);
			}
		};
		const cancelEdit = () => {
			router.push("/admin/event/all");
		};

		onMounted(() => {
			getCategories();
			fetchEvent();
			if (!organizations.value.length) getOrganizers();
			if (!userInfo.value) getInfo();
		});

		return {
			event,
			errors,
			isSubmitting,
			isLoading,
			isLoadingEvent,
			isValidForm,
			categories,
			selectedCategory,
			fileInput,
			selectedFiles,
			locationQuery,
			locationSuggestions,
			zoom,
			availableCategories,
			organizations,
			fetchLocationSuggestions,
			selectLocation,
			setLocation,
			addCategory,
			removeCategory,
			getCategoryName,
			addPrice,
			removePrice,
			isAmountFilled,
			browseFiles,
			handleDrop,
			handleImageUpload,
			resetImage,
			handleLogoUpload,
			uploadingIndex,
			addOrganizerLogo,
			removeOrganizerLogo,
			submitForm,
			confirmDelete,
			cancelEdit,
			forAdmin,
			forClient,
			userInfo,
			handleTextarea,
			charCount,
			maxChars,
			descriptionError,
			isValid,
			minChars,
			setMessage,
			exceedMaxChar,
			minimumChar,
			validateEventDateTime,
			cropperOpen,
			cropperSrc,
			cropperFileName,
			closeCropper,
			onCropDone,
		};
	},
};
</script>

<style scoped>
.form-label {
	@apply flex items-center gap-2 text-[12px] font-bold uppercase tracking-widest mb-2
         text-muted dark:text-muted-light;
}
.form-input {
	@apply w-full px-3.5 py-2.5 rounded-xl text-[13px] font-medium
         bg-primary-50/60 dark:bg-dark-600
         border border-primary-100 dark:border-dark-500
         text-dark-700 dark:text-white
         placeholder-muted dark:placeholder-muted-light
         outline-none transition-all duration-200
         focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20
         dark:focus:border-primary-400;
}
.form-divider {
	@apply flex items-center gap-3 my-1;
}
.form-divider::before,
.form-divider::after {
	content: "";
	@apply flex-1 border-t border-dashed border-primary-100 dark:border-dark-600;
}

/* Shimmer for logo upload */
@keyframes shimmer {
	0% {
		background-position: -400px 0;
	}
	100% {
		background-position: 400px 0;
	}
}
.skeleton-shimmer {
	background: linear-gradient(
		90deg,
		rgba(228, 244, 240, 0.6) 25%,
		rgba(208, 237, 232, 0.8) 37%,
		rgba(228, 244, 240, 0.6) 63%
	);
	background-size: 400px 100%;
	animation: shimmer 1.4s ease infinite;
}
</style>